import { useEffect, useState } from 'react';
import * as wasm from 'sched';
import { useDatabase } from './useDatabase';

export interface OutcomeDetails {
  id: string;
  title: string;
  description?: string;
  remoteReference?: wasm.RemoteReference;
  completionCriterion?: wasm.CompletionCriterion;
  deadlines?: wasm.Deadline[];
}

/**
 * Hook to access outcome data
 */
export function useOutcome(outcomeId: string | undefined) {
  const { database, loading: databaseLoading } = useDatabase();
  const [outcome, setOutcome] = useState<OutcomeDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!database || !outcomeId) {
      setLoading(false);
      return;
    }

    try {
      const outcomes = database.outcomes();
      const outcomeObj = outcomes[outcomeId];

      if (!outcomeObj) {
        setError(`Outcome with ID ${outcomeId} not found`);
        setOutcome(null);
      } else {
        // Fetch outcome data asynchronously
        const fetchOutcomeData = async () => {
          try {
            // Get remote reference
            const remoteRef = await outcomeObj.remote_reference();
            
            // Set outcome with all data
            setOutcome({
              id: outcomeId,
              title: outcomeObj.title(),
              description: outcomeObj.description(),
              remoteReference: remoteRef,
              completionCriterion: outcomeObj.completion_criterion(),
              deadlines: outcomeObj.deadlines()
            });
            setError(null);
          } catch (err) {
            console.error('Error fetching outcome data:', err);
            // Still set the outcome with basic data
            setOutcome({
              id: outcomeId,
              title: outcomeObj.title(),
              description: outcomeObj.description()
            });
            setError(null);
          } finally {
            setLoading(false);
          }
        };
        
        fetchOutcomeData();
      }
    } catch (err) {
      console.error('Error fetching outcome:', err);
      setError(`Error fetching outcome: ${err}`);
      setOutcome(null);
    } finally {
      setLoading(false);
    }
  }, [database, outcomeId]);

  return { outcome, loading: loading || databaseLoading, error };
}
